import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';
import { settingsValuesEqual } from '../utils/settingsDirty';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  DEFAULT_DUNNING_DEADLINE_DAYS,
  normalizeDunningDeadlineDays,
} from '../../invoices/utils/dunningDocument';

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30';

const DEADLINE_OPTIONS = [
  { value: '7', label: '7 Tage' },
  { value: '10', label: '10 Tage' },
  { value: '14', label: '14 Tage' },
];

type FormState = {
  dunning_deadline_days: number;
  dunning_show_creditreform: boolean;
};

function formFromUnknown(data: unknown): FormState {
  const row = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    dunning_deadline_days: normalizeDunningDeadlineDays(row.dunning_deadline_days),
    dunning_show_creditreform: row.dunning_show_creditreform === true,
  };
}

export function DunningSettingsSection() {
  const [form, setForm] = React.useState<FormState>({
    dunning_deadline_days: DEFAULT_DUNNING_DEADLINE_DAYS,
    dunning_show_creditreform: false,
  });
  const [editSnapshot, setEditSnapshot] = React.useState<FormState | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const editing = editSnapshot != null;
  const disabled = !editing || saving || !loaded;
  const dirty = editing && editSnapshot != null && !settingsValuesEqual(form, editSnapshot);

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setForm(formFromUnknown(data));
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const beginEdit = () => {
    setEditSnapshot({ ...form });
    setError(null);
    setSaved(false);
  };

  const cancelEdit = () => {
    if (editSnapshot) setForm(editSnapshot);
    setEditSnapshot(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Speichern fehlgeschlagen.');
      }
      const data = await res.json();
      setForm(formFromUnknown(data));
      setEditSnapshot(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Mahnung"
      description="Nachfrist für Mahnschreiben und Mahn-E-Mails (ab Briefdatum) sowie optional das Creditreform-Logo auf dem Mahnungsdokument. Anfangs- und Schlusssatz stellen Sie unter Dokumenttexte ein."
      actions={
        <>
          <SettingsCardSavedStatus saved={saved && !editing} />
          {editing ? (
            <>
              <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={cancelEdit} disabled={saving}>
                Abbrechen
              </button>
              <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => void save()} disabled={saving || !dirty}>
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : (
            <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={beginEdit} disabled={!loaded}>
              Bearbeiten
            </button>
          )}
        </>
      }
    >
      {error || loadError ? (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error || loadError}</div>
      ) : null}
      {!loaded && !loadError ? <InlineLoadingState label="Einstellungen werden geladen …" /> : null}
      {loaded ? (
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`} htmlFor="dunning-deadline-days">
              Neue Zahlungsfrist
            </label>
            <div className="flex flex-wrap items-center gap-2 max-w-md">
              <div className="w-24">
              <input
                id="dunning-deadline-days"
                type="number"
                min={1}
                max={30}
                inputMode="numeric"
                disabled={disabled}
                value={form.dunning_deadline_days}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    dunning_deadline_days: normalizeDunningDeadlineDays(e.target.value),
                  }))
                }
                className={SETTINGS_INPUT_CLASS}
              />
              </div>
              <span className="text-sm text-app-text-muted">Tage</span>
              {DEADLINE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      dunning_deadline_days: normalizeDunningDeadlineDays(opt.value),
                    }))
                  }
                  className={`inline-flex h-9 items-center rounded-md border px-3 text-sm ${
                    String(form.dunning_deadline_days) === opt.value
                      ? 'border-pipedrive-blue bg-blue-50 text-pipedrive-blue'
                      : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                  } disabled:opacity-40`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-app-text-subtle">
              1–30 Tage, gilt für alle Mahnstufen. Platzhalter {'{{DEADLINE}}'} im Mahnungsdokument verwendet dasselbe Datum.
            </p>
          </div>
          <div>
            <p className={`${SETTINGS_FIELD_LABEL} mb-3`}>Creditreform</p>
            <label
              htmlFor="dunning-show-creditreform"
              className={`flex items-center gap-2.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <input
                id="dunning-show-creditreform"
                type="checkbox"
                className={CHECKBOX_CLASS}
                checked={form.dunning_show_creditreform}
                disabled={disabled}
                onChange={(e) => setForm((f) => ({ ...f, dunning_show_creditreform: e.target.checked }))}
              />
              <span className="text-sm text-app-text">Creditreform-Logo auf dem Mahnungsdokument zeigen</span>
            </label>
          </div>
        </div>
      ) : null}
    </SettingsCard>
  );
}
